import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppDataTable } from '@components'
import { dsColorNew } from "@palette"

import { dorColumn, lorColumn, officeColumn, searchDateColumn, sitesColumn } from 'Pages/RealTimeHistory/History/Table/columns'


const DEMO_SEARCHES = [
	{
		searchDate: '2025-03-12 14:32:05',
		pickupOffice: { name: 'Madrid Barajas Airport', country: 'ES', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'swiftfleet.demo', 'eurodrive.demo'],
		dor: ['2025-03-15', '2025-03-22'],
		lor: [3, 5, 7],
	},
	{
		searchDate: '2025-03-11 10:05:41',
		pickupOffice: { name: 'Barcelona El Prat Airport', country: 'ES', icon: 'plane-departure' },
		dropoffOffice: { name: 'Madrid Barajas Airport', country: 'ES', icon: 'plane-arrival' },
		sites: ['rentalspot.demo', 'findwheels.demo', 'pricefly.demo', 'autolink.demo'],
		dor: ['2025-03-20'],
		lor: [5, 10],
	},
	{
		searchDate: '2025-03-10 17:48:12',
		pickupOffice: { name: 'London Heathrow Airport', country: 'GB', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'alphacars.demo', 'horizoncars.demo', 'peakrentals.demo'],
		dor: ['2025-04-01', '2025-04-05', '2025-04-10'],
		lor: [7],
	},
	{
		searchDate: '2025-03-09 09:12:30',
		pickupOffice: { name: 'Paris Charles de Gaulle', country: 'FR', icon: 'plane-departure' },
		dropoffOffice: null,
		sites: ['rentalspot.demo', 'findwheels.demo', 'swiftfleet.demo'],
		dor: ['2025-03-25'],
		lor: [3, 5],
	},
	{
		searchDate: '2025-03-08 12:20:58',
		pickupOffice: { name: 'Rome Downtown Office', country: 'IT', icon: 'city' },
		dropoffOffice: { name: 'Milan Central Station', country: 'IT', icon: 'train' },
		sites: ['rentalspot.demo', 'pricefly.demo', 'bookride.demo', 'eurodrive.demo', 'alphacars.demo'],
		dor: ['2025-04-15', '2025-04-20'],
		lor: [4, 7],
	},
]

const COUNTRY = { ES: 'Spain', GB: 'United Kingdom', FR: 'France', IT: 'Italy' }
const OFFICE_TYPE = { 'plane-departure': 'airport', 'plane-arrival': 'airport', city: 'downtown', train: 'train_station' }

// an office as the rows the real Office column takes
const officeRow = office => ({ name: office.name, country: COUNTRY[office.country], type: OFFICE_TYPE[office.icon] })

// The rows in the shape of the history API, so the table below is built with the page's own columns.
// Without a one-way drop-off the API repeats the pick-up office as the drop-off, as the real table shows.
const DEMO_ROWS = DEMO_SEARCHES.map(s => ({
	timestamp_create_user: s.searchDate,
	pickUp: [officeRow(s.pickupOffice)],
	dropDown: officeRow(s.dropoffOffice || s.pickupOffice),
	dropDownFilter: s.pickupOffice.name,
	sitesTranslationName: s.sites.map(name => ({ name })),
	id_site_translation: s.sites.join(', '),
	pickup_date_translation: s.dor,
	length_of_rental: s.lor,
	id_search: null,
}))

// Same cell as Table/columns/actions.jsx, without opening anything
const demoActionsColumn = {
	name: 'id_search',
	label: 'Real Time Searches',
	options: {
		filter: false,
		customBodyRowRenderLite: () => <Box sx={{ textAlign: 'center' }}>
			<AppButtonIcon
				sx={{ width: 32, height: 32, borderColor: dsColorNew.appBlue, fontSize: 22, color: dsColorNew.appBlue }}
				tooltip="View in Real Time Searches"
				icon="magnifying-glass-arrow-right"
			/>
		</Box>,
	},
}


// Same table as Pages/RealTimeHistory/History/Table. It steps aside when the user has real searches:
// the real "Historical List" is then the one the tour walks through.
export default function HistoryDemoData() {
	const ref = useRef(null)
	const [hasRealTable, setHasRealTable] = useState(false)

	useEffect(() => {
		const check = () => setHasRealTable([...document.querySelectorAll('table')].some(t =>
			!ref.current?.contains(t) && [...t.querySelectorAll('thead th')].some(th => th.textContent.trim() === 'Date of Search')))
		check()
		const observer = new MutationObserver(check)
		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [])

	return <Box ref={ref} sx={{ display: hasRealTable ? 'none' : 'block' }}>
		<AppDataTable
			title={<AppCardTitle icon="clock-rotate-left" title="Historical List"/>}
			toolbarProps={{ actions: false }}
			data={DEMO_ROWS}
			columns={[
				searchDateColumn(),
				officeColumn(),
				sitesColumn(),
				dorColumn(),
				lorColumn(),
				demoActionsColumn,
			]}
			setHeaderCellProps={() => ({ sx: { fontWeight: "bold" } })}
		/>
	</Box>
}
